import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import * as emailApi from '../../services/emailMarketingApi';
import { DownloadCloud, RefreshCw } from 'lucide-react';

const ExcelReportExport = ({ stats, onExportStart, onExportEnd }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState('');
  const [promotions, setPromotions] = useState([]);
  const [loadingPromotions, setLoadingPromotions] = useState(false);
  const [error, setError] = useState('');

  // Fetch unique promotions from the API
  useEffect(() => {
    const fetchPromotions = async () => {
      setLoadingPromotions(true);
      setError('');
      
      try {
        const response = await emailApi.getUniquePromotions();
        // Handle different response structures
        const promotionsList = response.data || response || [];
        setPromotions(Array.isArray(promotionsList) ? promotionsList : []);
        
        if (promotionsList.length === 0) {
          setError('No promotions found');
        }
      } catch (err) {
        console.error('Failed to fetch promotions:', err);
        setError('Failed to load promotions');
        setPromotions([]);
      } finally {
        setLoadingPromotions(false);
      }
    };

    fetchPromotions();
  }, []);

  const handleExport = async () => {
    if (!selectedPromotion) {
      alert('Please select a promotion filter first');
      return;
    }

    setIsExporting(true);
    setError('');
    if (onExportStart) onExportStart();
    
    try {
      const wb = XLSX.utils.book_new();

      // ── Sheet 1: Summary Stats ───────────────────────────────────────────
      const summaryData = [
        ['Metric', 'Value'],
        ['Promotion Filter', selectedPromotion],
        ['Report Generated', new Date().toLocaleString()],
        [],
        ['Email Logs Summary', ''],
      ];

      // Fetch summary stats for the selected promotion
      try {
        const statsResponse = await emailApi.getPromotionStats(selectedPromotion);
        const promotionStats = statsResponse.data || {};

        summaryData.push(
          ['Total Emails', promotionStats.total_emails || 0],
          ['Unique Opens', promotionStats.unique_opens || 0],
          ['Unique Clicks', promotionStats.unique_clicks || 0],
          ['Open Rate (%)', promotionStats.open_rate || 0],
          ['Click Rate (%)', promotionStats.click_rate || 0]
        );
      } catch (statsErr) {
        console.warn('Could not fetch promotion stats:', statsErr);
        summaryData.push(['Note', 'Detailed stats unavailable']);
      }

      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 22 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary Stats');

      // ── Sheet 2: Email Logs filtered by promotion ────────────────────────
      const logHeaders = [
        'Campaign Code', 'Campaign Subject',
        'Recipient Email', 'Recipient Name',
        'Group Name', 'Division',
        'Delivery Status', 'Opens', 'Clicks',
        'First Opened At', 'Last Clicked At', 'Delivered At', 'Error',
        'Promotion'
      ];
      
      const allLogRows = [];
      let page = 1;
      let hasMorePages = true;
      
      while (hasMorePages) {
        try {
          const logRes = await emailApi.getLogsByPromotion(selectedPromotion, page, 1000);
          const logData = logRes.data?.data || logRes.data || [];
          const lastPage = logRes.data?.last_page || logRes.last_page || 1;

          // Handle both array and object responses
          const logsArray = Array.isArray(logData) ? logData : (logData.data || []);

          for (const log of logsArray) {
            allLogRows.push([
              log.campaign_code || log.campaign?.code || '',
              log.campaign_subject || log.campaign?.subject || '',
              log.email || '',
              log.name || log.recipient_name || '',
              log.group_name || log.group?.name || '',
              log.division || '',
              log.status || log.delivery_status || '',
              log.open_count || log.opens || 0,
              log.click_count || log.clicks || 0,
              log.first_opened_at ? new Date(log.first_opened_at).toLocaleString() : '',
              log.last_clicked_at ? new Date(log.last_clicked_at).toLocaleString() : '',
              log.delivered_at || log.delivery_date ? new Date(log.delivered_at || log.delivery_date).toLocaleString() : '',
              log.error_message || log.error || '',
              log.promotion || selectedPromotion
            ]);
          }

          if (page >= lastPage) {
            hasMorePages = false;
          }
          page++;
        } catch (logErr) {
          console.error('Error fetching logs page:', logErr);
          hasMorePages = false;
        }
      }

      if (allLogRows.length === 0) {
        allLogRows.push(['No logs found for this promotion', '', '', '', '', '', '', '', '', '', '', '', '', '']);
      }

      const wsLogs = XLSX.utils.aoa_to_sheet([logHeaders, ...allLogRows]);
      wsLogs['!cols'] = [
        { wch: 16 }, { wch: 36 }, { wch: 34 }, { wch: 24 },
        { wch: 22 }, { wch: 22 },
        { wch: 14 }, { wch: 8 }, { wch: 8 },
        { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 30 },
        { wch: 20 }
      ];
      wsLogs['!freeze'] = { xSplit: 0, ySplit: 1 };
      
      const sheetName = `Logs - ${selectedPromotion}`.substring(0, 31);
      XLSX.utils.book_append_sheet(wb, wsLogs, sheetName);

      // ── Download ──────────────────────────────────────────────────────────
      const sanitizedPromotion = selectedPromotion.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const filename = `email-logs-${sanitizedPromotion}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);

    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export report: ' + (err.message || 'Unknown error'));
    } finally {
      setIsExporting(false);
      if (onExportEnd) onExportEnd();
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className="relative">
        <select
          value={selectedPromotion}
          onChange={(e) => {
            setSelectedPromotion(e.target.value);
            setError('');
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[200px]"
          disabled={loadingPromotions || isExporting}
        >
          <option value="">Select Promotion</option>
          {promotions.map((promotion) => (
            <option key={promotion} value={promotion}>
              {promotion}
            </option>
          ))}
        </select>
        {loadingPromotions && (
          <RefreshCw size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 animate-spin text-gray-400" />
        )}
      </div>
      
      <button
        onClick={handleExport}
        disabled={isExporting || !selectedPromotion || loadingPromotions}
        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm font-medium shadow-sm"
        title="Download email logs filtered by promotion"
      >
        {isExporting ? (
          <RefreshCw size={16} className="animate-spin" />
        ) : (
          <DownloadCloud size={16} />
        )}
        {isExporting ? 'Exporting...' : 'Download Logs'}
      </button>
      
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default ExcelReportExport;